import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import type { MessageResponse, ProspectInfo } from "../../../api/generated";

const QUICK_ACTIONS = [
  {
    label: "Research competitors",
    prompt: "Research the main competitors of {company} and how they compare.",
    fingerprint: "main competitors",
  },
  {
    label: "Recent news",
    prompt:
      "Find recent news, press releases, or announcements about {company} from the last 6 months.",
    fingerprint: "recent news",
  },
  {
    label: "Industry trends",
    prompt:
      "What are the key industry trends affecting {company} and their market?",
    fingerprint: "industry trends",
  },
  {
    label: "Potential objections",
    prompt:
      "What objections might {prospect} raise during this call, and how should I address them?",
    fingerprint: "objections",
  },
  {
    label: "LinkedIn insights",
    prompt:
      "Based on what you know about {prospect} at {company}, what insights from their background would be relevant for this call?",
    fingerprint: "insights from their background",
  },
  {
    label: "Draft Email",
    prompt:
      "Draft a personalized outreach email for {prospect} at {company}. Draw on our methodology and value proposition to connect with their likely challenges and priorities.",
    fingerprint: "outreach email",
  },
];

interface QuickActionsProps {
  prospect: ProspectInfo | null | undefined;
  messages: MessageResponse[];
  onSend: (message: string) => void;
  disabled?: boolean;
}

export function QuickActions({
  prospect,
  messages,
  onSend,
  disabled,
}: QuickActionsProps) {
  const fillTemplate = (template: string): string => {
    return template
      .replace(/\{prospect\}/g, prospect?.name || "the prospect")
      .replace(/\{company\}/g, prospect?.company || "the company");
  };

  // Build a lowercase string of all user messages for fingerprint matching
  const userText = messages
    .filter((m) => m.role === "user")
    .map((m) => m.content.toLowerCase())
    .join(" ");

  const unusedActions = QUICK_ACTIONS.filter(
    (a) => !userText.includes(a.fingerprint),
  );

  if (unusedActions.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        px: 2,
        pb: 1,
        overflowX: "auto",
        flexShrink: 0,
        "&::-webkit-scrollbar": { height: 4 },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 2,
          bgcolor: "action.hover",
        },
      }}
    >
      {unusedActions.map((action) => (
        <Chip
          key={action.label}
          label={action.label}
          size="small"
          variant="outlined"
          onClick={() => onSend(fillTemplate(action.prompt))}
          disabled={disabled}
          sx={{ flexShrink: 0 }}
        />
      ))}
    </Box>
  );
}
